/**
 * ============================================================================
 * FILTER PREDICATE — applies PropertyFilter tokens to DCR records.
 * ============================================================================
 *
 * QuickSight parity: in the CTS WBR dashboard every top-bar control is a
 * row-level filter on a view column (WHERE col IN (...)). Our equivalent
 * "rows" are the per-DCR records inside each getMetrics duration; this module
 * evaluates the Cloudscape PropertyFilter query against each DcrRecord BEFORE
 * aggregation, so every table/tile/card recomputes under filters exactly like
 * QuickSight visuals do.
 *
 * Token → DcrRecord field mapping (QuickSight column in parens):
 *   orderCategory → orderCategory      (order_category)
 *   customerType  → customerType       (is_internal_customer, e.g. 'AGIDS')
 *   customer      → customer           (customer_name)
 *   locale        → locale             (locale)
 *   channel       → channelInfo[].channel     (channel)
 *   subChannel    → channelInfo[].subChannel  (sub_channel)
 *   domain        → domain             (domain)
 *   dcrStatus     → status             (dcr_status)
 *   deliveryType  → deliveryType       (delivery_type)
 *   uom           → unitOfMeasure      (unit_of_measure)
 *   dcrId         → dcrId              (dcr_id)
 *   backupDcr     → isBackupDcr        (is_backup_dcr)      Yes/No
 *   byocDcr       → isByocDcr          (is_byoc_dcr)        Yes/No
 *   otsDcr        → isOtsDcr           (is_ots_dcr)         Yes/No
 *   byotOfflineDcr→ isByotOfflineDcr   (is_byot_offline_dcr) Yes/No
 *
 * NOT row-level (handled elsewhere, like QuickSight parameters):
 *   reportPeriod (WBR/MBR cadence — the API's duration grain)
 *   metricGroup (filters metric rows, not DCRs)
 *   onTimeDeliveryThreshold (a parameter for % DCRs >= threshold calc)
 */

import { DcrRecord } from 'src/types/metricsApi.types';
import { FilterQuery } from 'src/features/dashboard/dashboardSlice';

type TokenOperator = '=' | '!=' | ':' | '!:' | '>=' | '<=' | string;

interface FilterToken {
  propertyKey?: string;
  operator: TokenOperator;
  value: string;
}

/**
 * Token keys that filter DCR rows (everything else is ignored here).
 * Exported: filterOptions.ts derives the data-driven dropdown values from
 * the same accessors, so a filterable field and its options can never drift.
 */
/** Boolean DCR flags render as the QuickSight control's Yes/No values. */
function yesNo(flag: boolean | undefined): string[] {
  return [flag ? 'Yes' : 'No'];
}

/**
 * Split a comma-joined wire value into atomic values ("a, b,c" → a, b, c).
 *
 * Channel, Sub Channel and Domain arrive comma-joined when a DCR has several
 * (verified against prod data). The legacy QuickSight dashboard offers the
 * composite string as ONE dropdown entry ("1P_DA, 3P_VENDOR"), which
 * fragments the value space: the same channel appears in many composites and
 * selecting one composite misses DCRs that carry the channel in another.
 * We deliberately DIVERGE from parity here: options and matching are atomic,
 * so selecting `b` matches every DCR whose list contains `b`
 * (spec filter-atomization-metric-name R1).
 */
function splitMultiValue(raw: string | undefined): string[] {
  return (raw ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** splitMultiValue over a list of raw values (es5-lib-safe flatMap). */
function splitMultiValues(raws: Array<string | undefined>): string[] {
  const out: string[] = [];
  for (const raw of raws) {
    for (const value of splitMultiValue(raw)) out.push(value);
  }
  return out;
}

export const DCR_FIELD_ACCESSORS: Record<string, (dcr: DcrRecord) => string[]> =
  {
    orderCategory: (dcr) => [dcr.orderCategory ?? ''],
    // customerType is INTERNAL | AGIDS on the wire; tokenMatches compares
    // case-insensitively, so the 'AGIDS' / 'Internal' control values both work.
    customerType: (dcr) => [dcr.customerType ?? ''],
    customer: (dcr) => [dcr.customer ?? ''],
    locale: (dcr) => [dcr.locale ?? ''],
    // Channel / Sub Channel / Domain are ATOMIZED (splitMultiValue): the wire
    // may carry comma-joined composites, and both the dropdown options and
    // row matching operate on the individual values — `=` means "the DCR's
    // list contains the value". Locale is already atomic on the wire.
    domain: (dcr) => splitMultiValue(dcr.domain),
    dcrStatus: (dcr) => [dcr.status ?? ''],
    deliveryType: (dcr) => [dcr.deliveryType ?? ''],
    uom: (dcr) => [dcr.unitOfMeasure ?? ''],
    dcrId: (dcr) => [dcr.dcrId],
    channel: (dcr) =>
      splitMultiValues((dcr.channelInfo ?? []).map((c) => c.channel)),
    subChannel: (dcr) =>
      splitMultiValues((dcr.channelInfo ?? []).map((c) => c.subChannel)),
    backupDcr: (dcr) => yesNo(dcr.isBackupDcr),
    byocDcr: (dcr) => yesNo(dcr.isByocDcr),
    otsDcr: (dcr) => yesNo(dcr.isOtsDcr),
    backfillDcr: (dcr) => yesNo(dcr.isBackfillDcr),
    byotOfflineDcr: (dcr) => yesNo(dcr.isByotOfflineDcr),
    // Deep-dive controls (spec deep-dive-periodical R4.2). Demand Category is a
    // FILTER on that tab, where it is structural (one section per category) on
    // the Detailed Summary — hence a chip here and a checkbox row there.
    demandCategory: (dcr) => [dcr.demandCategory ?? ''],
    committedWeek: (dcr) => [dcr.committedWeek ?? ''],
    needByWeek: (dcr) => [dcr.needByWeek ?? ''],
    // DCR Created Date Range (QuickSight dateTimePicker parity): a RANGE via
    // >= / <= chips on ISO calendar dates, typed by the user — no options are
    // offered (a distinct-date dropdown would be hundreds of noise entries;
    // see NO_OPTION_KEYS in filterOptions).
    dcrCreatedDate: (dcr) => [dcr.dcrCreatedDate ?? ''],
  };

/**
 * A DCR field's values trimmed + lowercased once per row, matching how
 * matchesDemandCategory compares, so chip and tile paths agree on padded values.
 */
function normalizedValues(
  accessor: (dcr: DcrRecord) => string[],
  dcr: DcrRecord,
): string[] {
  return accessor(dcr).map((v) => v.trim().toLowerCase());
}

/**
 * One non-equality token against a field's normalized values. Range
 * operators are declared only by dcrCreatedDate (ISO dates: lexicographic
 * order is date order); a missing value ('') never passes a range.
 */
function valuesMatch(
  values: string[],
  operator: TokenOperator,
  target: string,
): boolean {
  switch (operator) {
    case '!=':
      return !values.some((v) => v === target);
    case ':':
      return values.some((v) => v.includes(target));
    case '!:':
      return !values.some((v) => v.includes(target));
    case '>=':
      return values.some((v) => v !== '' && v >= target);
    case '<=':
      return values.some((v) => v !== '' && v <= target);
    default:
      return true;
  }
}

/**
 * A property's tokens compiled for O(1) equality matching. `=` values go in
 * a Set so a multi-select of thousands of DCR IDs costs one lookup per row
 * instead of one comparison per token; other operators stay as a short list.
 */
/** Operators that AND across a property instead of OR-ing (see buildDcrPredicate). */
const NEGATIVE_OPERATORS = new Set<TokenOperator>(['!=', '!:', '>=', '<=']);

interface CompiledProperty {
  accessor: (dcr: DcrRecord) => string[];
  equals: Set<string>;
  others: Array<{ operator: TokenOperator; target: string }>;
}

function compileByProperty(tokens: FilterToken[]): CompiledProperty[] {
  const byKey: Record<string, CompiledProperty> = {};
  for (const token of tokens) {
    const key = token.propertyKey as string;
    let compiled = byKey[key];
    if (!compiled) {
      compiled = byKey[key] = {
        accessor: DCR_FIELD_ACCESSORS[key],
        equals: new Set<string>(),
        others: [],
      };
    }
    const target = token.value.trim().toLowerCase();
    if (token.operator === '=') compiled.equals.add(target);
    else compiled.others.push({ operator: token.operator, target });
  }
  return Object.keys(byKey).map((key) => byKey[key]);
}

/** OR within a property: any `=` value present, or any other token matching. */
function propertyMatchesAny(
  compiled: CompiledProperty,
  dcr: DcrRecord,
): boolean {
  const values = normalizedValues(compiled.accessor, dcr);
  if (compiled.equals.size > 0 && values.some((v) => compiled.equals.has(v)))
    return true;
  return compiled.others.some((t) => valuesMatch(values, t.operator, t.target));
}

/**
 * The Metric Group selection. Unlike every other control this filters METRIC
 * ROWS, not DCR rows, so it can't go through buildDcrPredicate — selectors
 * apply it to the metric catalog instead. It applies to EVERY metric type:
 * the legacy dashboard's "OTD visuals ignore the control" behaviour was a
 * QuickSight limitation, not a feature, and is deliberately NOT copied.
 *
 * Multi-select semantics (Cloudscape + QuickSight): tokens on the SAME
 * property OR together. Two `=` tokens mean "group is A or B", never
 * "A and B" (which would match nothing).
 */
export interface MetricGroupSelection {
  /** Empty = every group passes. Values are normalizeGroupToken'd. */
  include: Set<string>;
  exclude: Set<string>;
}

/**
 * One normal form for group/preset comparison: "WBR Metrics Only" (the chip
 * label, from QuickSight's control) and "WBR_Metrics_Only" (the catalog's
 * preset wire value) are the SAME selection — underscores read as spaces.
 */
function normalizeGroupToken(value: string | undefined): string {
  return (value ?? '').replace(/_/g, ' ').trim().toLowerCase();
}

export function selectedMetricGroups(
  query: FilterQuery | undefined,
): MetricGroupSelection {
  const include = new Set<string>();
  const exclude = new Set<string>();
  for (const token of (query?.tokens ?? []) as FilterToken[]) {
    if (token.propertyKey !== 'metricGroup') continue;
    if (token.operator === '=') include.add(normalizeGroupToken(token.value));
    if (token.operator === '!=') exclude.add(normalizeGroupToken(token.value));
  }
  return { include, exclude };
}

/**
 * True when a metric passes the Metric Group selection. A definition matches
 * a selected value through EITHER its native metricGroup OR a preset
 * membership: in the real catalog "WBR Metrics Only" is not a group — WBR
 * membership is the `presets: ["WBR_Metrics_Only"]` tag, while values like
 * "Core Business Metrics" are native groups. Matching both sides makes the
 * chip values from QuickSight's static list work against the real catalog.
 */
export function metricGroupMatches(
  definition: { metricGroup?: string; presets?: string[] },
  selection: MetricGroupSelection | undefined,
): boolean {
  if (!selection) return true;
  const memberships = [normalizeGroupToken(definition.metricGroup)];
  for (const preset of definition.presets ?? []) {
    memberships.push(normalizeGroupToken(preset));
  }
  if (memberships.some((m) => selection.exclude.has(m))) return false;
  return (
    selection.include.size === 0 ||
    memberships.some((m) => selection.include.has(m))
  );
}

/**
 * The Metric Name selection (QuickSight's "Metric Name" control, which
 * cascades off Metric Group). Like Metric Group it filters METRIC ROWS, not
 * DCR rows — but unlike the broad group SCOPE (which spares percentage
 * metrics), a name is an explicit pick, so it applies to EVERY metric row
 * including percentages: choosing "Demand" means seeing Demand only.
 */
export interface MetricNameSelection {
  /** Empty = every name passes. */
  include: Set<string>;
  exclude: Set<string>;
}

export function selectedMetricNames(
  query: FilterQuery | undefined,
): MetricNameSelection {
  const include = new Set<string>();
  const exclude = new Set<string>();
  for (const token of (query?.tokens ?? []) as FilterToken[]) {
    if (token.propertyKey !== 'metricName') continue;
    if (token.operator === '=') include.add(token.value.trim().toLowerCase());
    if (token.operator === '!=') exclude.add(token.value.trim().toLowerCase());
  }
  return { include, exclude };
}

/** True when a metric's catalog name passes the Metric Name selection. */
export function metricNameMatches(
  metricName: string | undefined,
  selection: MetricNameSelection | undefined,
): boolean {
  if (!selection) return true;
  const name = (metricName ?? '').trim().toLowerCase();
  if (selection.exclude.has(name)) return false;
  return selection.include.size === 0 || selection.include.has(name);
}

/**
 * The period-level selection: which reporting periods the deep dive shows.
 *
 * These CANNOT go through buildDcrPredicate — that takes a DcrRecord, and these
 * filter the period wrapper around it (spec deep-dive-periodical R4.3, R4.4).
 *
 * - `periods`: chosen Time Period values; empty = every period.
 * - `recentOnly`: the "Recent Periods Only" control. Default Yes, matching the
 *   live dashboard, where it restricts to the service's deep-dive window.
 */
export interface PeriodSelection {
  periods: Set<string>;
  recentOnly: boolean;
}

export function selectedPeriodFilter(
  query: FilterQuery | undefined,
): PeriodSelection {
  const periods = new Set<string>();
  let recentOnly = true; // live default: "Recent Periods Only" = Yes
  for (const token of (query?.tokens ?? []) as FilterToken[]) {
    if (token.propertyKey === 'timePeriod' && token.operator === '=') {
      periods.add(token.value);
    }
    if (token.propertyKey === 'recentPeriodsOnly') {
      recentOnly = token.value.toLowerCase() !== 'no';
    }
  }
  return { periods, recentOnly };
}

/**
 * Build a DCR predicate from the PropertyFilter query. Tokens on the same
 * property OR together (multi-select); different properties AND together.
 * An explicit 'or' operation ORs everything. Unknown property keys never
 * constrain.
 */
export function buildDcrPredicate(
  query: FilterQuery | undefined,
): (dcr: DcrRecord) => boolean {
  const tokens = (query?.tokens ?? []) as FilterToken[];
  const dcrTokens = tokens.filter(
    (t) => t.propertyKey && DCR_FIELD_ACCESSORS[t.propertyKey],
  );
  if (dcrTokens.length === 0) return () => true;

  if (query?.operation === 'or') {
    const groups = compileByProperty(dcrTokens);
    return (dcr) => groups.some((group) => propertyMatchesAny(group, dcr));
  }

  // AND across properties, OR within a property. Two "Customer = X" /
  // "Customer = Y" chips mean either customer, not both at once — matching
  // Cloudscape multi-select and QuickSight's controls. Negations still AND
  // (excluding X and Y excludes both), and so do RANGE bounds: a
  // `dcrCreatedDate >= A` + `<= B` pair is the intersection [A, B] — OR'ing
  // them would pass every record.
  const negatives = compileByProperty(
    dcrTokens.filter((t) => NEGATIVE_OPERATORS.has(t.operator)),
  );
  const positives = compileByProperty(
    dcrTokens.filter((t) => !NEGATIVE_OPERATORS.has(t.operator)),
  );

  return (dcr) => {
    for (const group of negatives) {
      const values = normalizedValues(group.accessor, dcr);
      for (const t of group.others) {
        if (!valuesMatch(values, t.operator, t.target)) return false;
      }
    }
    for (const group of positives) {
      if (!propertyMatchesAny(group, dcr)) return false;
    }
    return true;
  };
}
